/** @format */

import React, { useRef, useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    Container,
    Grid,
    Stack,
    TextField,
    CircularProgress,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../../axiosConfig/apiUrl";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import SmsIcon from "@mui/icons-material/Sms";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import HandshakeIcon from "@mui/icons-material/Handshake";
import LowPriorityIcon from "@mui/icons-material/LowPriority";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TimelineIcon from "@mui/icons-material/Timeline";
import InsightsIcon from "@mui/icons-material/Insights";
import DonutLargeIcon from "@mui/icons-material/DonutLarge";
import PsychologyIcon from "@mui/icons-material/Psychology";
import BoltIcon from "@mui/icons-material/Bolt";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GroupsIcon from "@mui/icons-material/Groups";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import { colors } from "../../utility/types/constant";
import { Footer, SEO } from "../allComponents/AllComponents";
import { BusinessNavbar } from "../../components-v2/navbar/BusinessNavbar";

// ── Animations ───────────────────────────────────────────────────────────────

const floatY = keyframes`
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-14px); }
`;
const floatYSlow = keyframes`
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-22px); }
`;
const gradientPan = keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
`;
const pulseRing = keyframes`
    0% { transform: scale(0.85); opacity: 0.65; }
    70% { transform: scale(1.7); opacity: 0; }
    100% { opacity: 0; }
`;
const marquee = keyframes`
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
`;
const spinSlow = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;
const bounceCard = keyframes`
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-14px); }
`;

// ── Styled components ────────────────────────────────────────────────────────

const PageWrapper = styled("div")({
    background: "#ffffff",
    overflowX: "hidden",
});

const Hero = styled("section")({
    position: "relative",
    background:
        "linear-gradient(125deg, #070e29 0%, #0d1636 45%, #16245c 100%)",
    backgroundSize: "200% 200%",
    animation: `${gradientPan} 20s ease infinite`,
    padding: "clamp(90px, 11vw, 132px) 20px clamp(96px, 12vw, 140px)",
    overflow: "hidden",
});

const GridOverlay = styled("div")({
    position: "absolute",
    inset: 0,
    backgroundImage:
        "linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)",
    backgroundSize: "46px 46px",
    maskImage: "radial-gradient(ellipse 70% 60% at 50% 35%, #000 0%, transparent 80%)",
    WebkitMaskImage:
        "radial-gradient(ellipse 70% 60% at 50% 35%, #000 0%, transparent 80%)",
    pointerEvents: "none",
});

const Orb = styled("div")({
    position: "absolute",
    borderRadius: "50%",
    filter: "blur(60px)",
    pointerEvents: "none",
});

const Badge = styled("span")({
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    background: "rgba(255,204,0,0.12)",
    color: "#ffcc00",
    border: "1px solid rgba(255,204,0,0.30)",
    borderRadius: "100px",
    padding: "6px 16px",
    fontSize: "0.72rem",
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
});

const GoldSpan = styled("span")({
    background: "linear-gradient(135deg, #ffd02e 0%, #ffaa00 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
});

const TealSpan = styled("span")({
    background: "linear-gradient(135deg, #34e0ad 0%, #1ec195 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
});

const GoldButton = styled(Button)({
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    borderRadius: "12px",
    padding: "15px 32px",
    textTransform: "none",
    fontSize: "1rem",
    background: "linear-gradient(135deg, #ffd02e 0%, #ffaa00 100%)",
    color: "#0d1636",
    boxShadow: "0 10px 30px rgba(255,176,0,0.42)",
    "&:hover": {
        background: "linear-gradient(135deg, #ffe05a 0%, #ffbb1a 100%)",
        boxShadow: "0 16px 40px rgba(255,176,0,0.55)",
        transform: "translateY(-2px)",
    },
    transition: "all 0.22s ease",
});

const Section = styled("section")<{ tone?: "light" | "tint" | "dark" }>(
    ({ tone = "light" }) => ({
        position: "relative",
        overflow: "hidden",
        padding: "clamp(64px, 9vw, 104px) 0",
        background:
            tone === "dark"
                ? "linear-gradient(165deg, #0a1130 0%, #111e4a 100%)"
                : tone === "tint"
                    ? "#f6f8fc"
                    : "#ffffff",
    }),
);

const SectionLabel = styled(Typography, {
    shouldForwardProp: (prop) => prop !== "ondark",
})<{ ondark?: number }>(({ ondark }) => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: ondark ? "rgba(255,204,0,0.85)" : colors.mainSecondary,
    marginBottom: "12px",
    textAlign: "center",
}));

const SectionTitle = styled(Typography, {
    shouldForwardProp: (prop) => prop !== "ondark",
})<{ ondark?: number }>(({ ondark }) => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "clamp(1.6rem, 3.4vw, 2.5rem)",
    fontWeight: 800,
    color: ondark ? "#ffffff" : colors.primary,
    lineHeight: 1.16,
    letterSpacing: "-0.02em",
    textAlign: "center",
    marginBottom: "14px",
}));

const SectionSub = styled(Typography, {
    shouldForwardProp: (prop) => prop !== "ondark",
})<{ ondark?: number }>(({ ondark }) => ({
    fontFamily: "'Inter', sans-serif",
    fontSize: "clamp(0.95rem, 1.6vw, 1.06rem)",
    color: ondark ? "rgba(255,255,255,0.62)" : "#5b6472",
    lineHeight: 1.72,
    textAlign: "center",
    maxWidth: "600px",
    margin: "0 auto 56px",
}));

const FeatureCard = styled("div")<{ accent: string }>(({ theme, accent }) => ({
    position: "relative",
    background: "#ffffff",
    borderRadius: "18px",
    padding: "30px 26px",
    border: "1px solid rgba(4,13,69,0.07)",
    height: "100%",
    boxShadow: "0 1px 2px rgba(4,13,69,0.04)",
    transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
    overflow: "hidden",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "3px",
        background: accent,
        transform: "scaleX(0)",
        transformOrigin: "left",
        transition: "transform 0.3s ease",
    },
    "&:hover": {
        transform: "translateY(-6px)",
        boxShadow: "0 22px 48px rgba(4,13,69,0.14)",
        borderColor: `${accent}66`,
    },
    "&:hover::before": { transform: "scaleX(1)" },
    [theme.breakpoints.down("sm")]: { padding: "24px 20px" },
}));

const IconWrap = styled("div")<{ accent: string }>(({ accent }) => ({
    width: 48,
    height: 48,
    borderRadius: "13px",
    background: `${accent}1a`,
    border: `1px solid ${accent}33`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "18px",
    color: accent,
}));

const StepConnector = styled("div")(({ theme }) => ({
    position: "absolute",
    top: "34px",
    left: "calc(50% + 36px)",
    right: "calc(-50% + 36px)",
    height: "2px",
    background:
        "repeating-linear-gradient(90deg, rgba(30,193,149,0.4) 0 8px, transparent 8px 16px)",
    [theme.breakpoints.down("md")]: { display: "none" },
}));

const BarTrack = styled("div")({
    height: "9px",
    background: "rgba(255,255,255,0.09)",
    borderRadius: "100px",
    overflow: "hidden",
});

const MockCard = styled("div")({
    position: "relative",
    background: "linear-gradient(180deg, #ffffff 0%, #f5f8ff 100%)",
    borderRadius: "20px",
    border: "1px solid rgba(255,255,255,0.5)",
    boxShadow: "0 30px 80px rgba(2,8,40,0.55)",
    padding: "20px",
    width: "100%",
    maxWidth: "440px",
});

const FloatChip = styled("div")({
    position: "absolute",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    background: "#ffffff",
    borderRadius: "14px",
    padding: "11px 14px",
    boxShadow: "0 16px 36px rgba(2,8,40,0.28)",
    border: "1px solid rgba(4,13,69,0.06)",
    zIndex: 3,
});

// Browser-style frame that wraps the product-tour screenshots.
const BrowserFrame = styled("div")({
    borderRadius: "16px",
    overflow: "hidden",
    background: "#0b1533",
    border: "1px solid rgba(255,255,255,0.12)",
    boxShadow: "0 40px 90px rgba(2,8,40,0.55)",
});

const BrowserBar = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: "7px",
    padding: "12px 16px",
    background: "rgba(255,255,255,0.04)",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
});

const BrowserDot = styled("span")<{ c: string }>(({ c }) => ({
    width: 11,
    height: 11,
    borderRadius: "50%",
    background: c,
    flexShrink: 0,
}));

// ── Reveal-on-scroll helper ──────────────────────────────────────────────────

const Reveal: React.FC<{
    children: React.ReactNode;
    delay?: number;
    y?: number;
    sx?: object;
}> = ({ children, delay = 0, y = 26, sx }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [shown, setShown] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const ob = new IntersectionObserver(
            ([e]) => {
                if (e.isIntersecting) {
                    setShown(true);
                    ob.disconnect();
                }
            },
            { threshold: 0.15 },
        );
        ob.observe(el);
        return () => ob.disconnect();
    }, []);
    return (
        <Box
            ref={ref}
            sx={{
                opacity: shown ? 1 : 0,
                transform: shown ? "translateY(0)" : `translateY(${y}px)`,
                transition: `opacity 0.7s ease ${delay}s, transform 0.7s cubic-bezier(0.22,0.61,0.36,1) ${delay}s`,
                "@media (prefers-reduced-motion: reduce)": {
                    opacity: 1,
                    transform: "none",
                    transition: "none",
                },
                ...sx,
            }}
        >
            {children}
        </Box>
    );
};

// ── Count-up stat ────────────────────────────────────────────────────────────

const StatCounter: React.FC<{
    to: number;
    prefix?: string;
    suffix?: string;
    duration?: number;
}> = ({ to, prefix = "", suffix = "", duration = 1700 }) => {
    const ref = useRef<HTMLSpanElement>(null);
    const [val, setVal] = useState(0);
    const started = useRef(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const ob = new IntersectionObserver(
            ([e]) => {
                if (e.isIntersecting && !started.current) {
                    started.current = true;
                    const t0 = performance.now();
                    const tick = (now: number) => {
                        const p = Math.min(1, (now - t0) / duration);
                        const eased = 1 - Math.pow(1 - p, 3);
                        setVal(to * eased);
                        if (p < 1) requestAnimationFrame(tick);
                        else setVal(to);
                    };
                    requestAnimationFrame(tick);
                }
            },
            { threshold: 0.4 },
        );
        ob.observe(el);
        return () => ob.disconnect();
    }, [to, duration]);
    return (
        <span ref={ref}>
            {prefix}
            {Math.round(val).toLocaleString()}
            {suffix}
        </span>
    );
};

// ── Content data ─────────────────────────────────────────────────────────────

// Visual-only metadata — copy lives in i18n (business.landing.*).
const FEATURE_VISUALS = [
    { icon: <LowPriorityIcon />, accent: "#143AA2" },
    { icon: <NotificationsActiveIcon />, accent: "#ffb300" },
    { icon: <SmsIcon />, accent: "#1ec195" },
    { icon: <PsychologyIcon />, accent: "#a78bfa" },
    { icon: <HandshakeIcon />, accent: "#ff7a45" },
    { icon: <InsightsIcon />, accent: "#38bdf8" },
];

const STEP_ICONS = [
    <AccountBalanceOutlinedIcon />,
    <BoltIcon />,
    <TrendingUpIcon />,
];

// Audience section — visual-only metadata (copy lives in i18n).
const AUDIENCE_VISUALS = [
    { icon: <BoltIcon />, accent: "#ffb300" },
    { icon: <AutoAwesomeIcon />, accent: "#a78bfa" },
    { icon: <GroupsIcon />, accent: "#143AA2" },
    { icon: <AccountBalanceOutlinedIcon />, accent: "#1ec195" },
    { icon: <HandshakeIcon />, accent: "#ff7a45" },
    { icon: <LinkOutlinedIcon />, accent: "#38bdf8" },
];

// Data-control section — visual-only metadata (copy lives in i18n).
const DATACONTROL_VISUALS = [
    { icon: <VerifiedUserOutlinedIcon />, accent: "#1ec195" },
    { icon: <ShieldOutlinedIcon />, accent: "#143AA2" },
    { icon: <LockOutlinedIcon />, accent: "#a78bfa" },
    { icon: <LinkOutlinedIcon />, accent: "#ffb300" },
];

// One-invoice demo timeline — visual-only metadata (copy lives in i18n).
const DEMO_VISUALS = [
    { icon: <LowPriorityIcon />, accent: "#ef4444" },
    { icon: <NotificationsActiveIcon />, accent: "#ffb300" },
    { icon: <PsychologyIcon />, accent: "#a78bfa" },
    { icon: <HandshakeIcon />, accent: "#143AA2" },
    { icon: <CheckCircleIcon />, accent: "#1ec195" },
];

const STAT_VISUALS = [
    { to: 59, prefix: "", suffix: "%" },
    { to: 17700, prefix: "$", suffix: "" },
    { to: 39, prefix: "", suffix: "%" },
];

// Scoring section — visual-only metadata (copy lives in i18n).
const SCORING_VISUALS = [
    { icon: <VerifiedUserOutlinedIcon />, accent: "#1ec195" },
    { icon: <SpeedOutlinedIcon />, accent: "#143AA2" },
    { icon: <AccountTreeOutlinedIcon />, accent: "#a78bfa" },
    { icon: <QueryStatsOutlinedIcon />, accent: "#ffb300" },
];

// Sample reliability rows for the scoring mock (demo data, not translated).
const RELIABILITY_ROWS = [
    { name: "Northwind Studio", pct: 94, tone: "#1ec195", tier: "high" as const },
    { name: "Bayside Catering", pct: 71, tone: "#ff9f1c", tier: "watch" as const },
    { name: "Lumen Design Co.", pct: 48, tone: "#ef4444", tier: "risk" as const },
];

// Sample sparkline heights for the recovery-metrics mock.
const METRIC_SPARK = [38, 52, 46, 64, 78, 92];

// Product-tour screenshots — visual-only metadata (copy lives in i18n).
// Images live in front-end/public/images/how-it-works-images/.
const TOUR_SHOTS = [
    {
        img: "/images/how-it-works-images/Dashboard-Overview.png",
        icon: <InsightsIcon />,
        accent: "#1ec195",
    },
    {
        img: "/images/how-it-works-images/Recovery-Dashboard-Section.png",
        icon: <TimelineIcon />,
        accent: "#38bdf8",
    },
    {
        img: "/images/how-it-works-images/Recovery-Queue.png",
        icon: <LowPriorityIcon />,
        accent: "#ffb300",
    },
    {
        img: "/images/how-it-works-images/Never-Lose-Track.png",
        icon: <HandshakeIcon />,
        accent: "#a78bfa",
    },
    {
        img: "/images/how-it-works-images/recommendations.png",
        icon: <GroupsIcon />,
        accent: "#ff7a45",
    },
    {
        img: "/images/how-it-works-images/prediction.png",
        icon: <VerifiedUserOutlinedIcon />,
        accent: "#143AA2",
    },
];

// ── Component ─────────────────────────────────────────────────────────────────

const BusinessLandingPage: React.FC = () => {
    const { t } = useTranslation("business");
    const navigate = useNavigate();
    const location = useLocation();
    const [barsVisible, setBarsVisible] = useState(false);
    const impactRef = useRef<HTMLDivElement>(null);

    // Active screenshot in the product-tour showcase.
    const [activeTour, setActiveTour] = useState(0);

    // Hero "Request a demo" email capture — reuses the survey notification path.
    const [demoEmail, setDemoEmail] = useState("");
    const [demoSubmitting, setDemoSubmitting] = useState(false);
    const [demoSubmitted, setDemoSubmitted] = useState(false);
    const [demoError, setDemoError] = useState("");

    const demoEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(demoEmail.trim());

    const handleDemoRequest = async () => {
        if (!demoEmailValid || demoSubmitting) return;
        setDemoSubmitting(true);
        setDemoError("");
        try {
            await axios.post(`${API_BASE_URL}/feedback/business-survey`, {
                biggestFrustration:
                    "[Demo request] Requested a FundsyFi Recover demo from the business landing page.",
                earlyAccessEmail: demoEmail.trim(),
            });
            setDemoSubmitted(true);
        } catch {
            setDemoError(t("landing.hero.demoError"));
        } finally {
            setDemoSubmitting(false);
        }
    };

    const featureItems = t("landing.features.items", {
        returnObjects: true,
    }) as Array<{ title: string; desc: string }>;
    const stepItems = t("landing.steps.items", {
        returnObjects: true,
    }) as Array<{ title: string; desc: string }>;
    const tourItems = t("landing.tour.items", {
        returnObjects: true,
    }) as Array<{ tab: string; title: string; desc: string }>;
    const trustItems = t("landing.trust", { returnObjects: true }) as string[];
    const statLabels = t("landing.stats.items", {
        returnObjects: true,
    }) as string[];
    const barLabels = t("landing.stats.bars", {
        returnObjects: true,
    }) as string[];
    const controlPoints = t("landing.control.points", {
        returnObjects: true,
    }) as string[];
    const audienceItems = t("landing.audience.items", {
        returnObjects: true,
    }) as Array<{ title: string; desc: string }>;
    const dataControlPoints = t("landing.dataControl.points", {
        returnObjects: true,
    }) as Array<{ title: string; desc: string }>;
    const demoSteps = t("landing.demo.steps", {
        returnObjects: true,
    }) as Array<{ tag: string; title: string; desc: string }>;
    const setupItems = t("landing.hero.setupSteps", {
        returnObjects: true,
    }) as string[];
    const onboardingItems = t("landing.onboarding.items", {
        returnObjects: true,
    }) as string[];
    const faqItems = t("landing.faq.items", {
        returnObjects: true,
    }) as Array<{ q: string; a: string }>;
    const scoringCards = t("landing.scoring.cards", {
        returnObjects: true,
    }) as Array<{ title: string; desc: string }>;
    const tierLabels: Record<"high" | "watch" | "risk", string> = {
        high: t("landing.scoring.tierHigh"),
        watch: t("landing.scoring.tierWatch"),
        risk: t("landing.scoring.tierRisk"),
    };
    const aiPoints = [
        t("landing.ai.point1"),
        t("landing.ai.point2"),
        t("landing.ai.point3"),
    ];

    const QUEUE_ROWS = [
        { name: "Northwind Studio", amount: "$4,200", days: "32d", pct: 88, tone: "#ef4444" },
        { name: "Bayside Catering", amount: "$1,850", days: "18d", pct: 64, tone: "#ff9f1c" },
        { name: "Lumen Design Co.", amount: "$960", days: "7d", pct: 38, tone: "#1ec195" },
    ];

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) setBarsVisible(true);
            },
            { threshold: 0.25 },
        );
        if (impactRef.current) observer.observe(impactRef.current);
        return () => observer.disconnect();
    }, []);

    // Land at the top when arriving without an anchor; scroll to the
    // section when a hash (e.g. #features) is present.
    useEffect(() => {
        if (location.hash) {
            const id = location.hash.replace("#", "");
            const el = document.getElementById(id);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                return;
            }
        }
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, [location.pathname, location.hash]);
    // Shared "Request a demo" email-capture form — used in the hero and final CTA.
    const renderDemoForm = (centered = false) =>
        demoSubmitted ? (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.6,
                    background: "rgba(30,193,149,0.12)",
                    border: "1px solid rgba(30,193,149,0.35)",
                    borderRadius: "14px",
                    p: 2.2,
                    maxWidth: "520px",
                    mx: centered ? "auto" : 0,
                    mb: 3,
                    textAlign: "left",
                }}
            >
                <CheckCircleIcon
                    sx={{
                        fontSize: "1.5rem",
                        color: colors.mainSecondary,
                        flexShrink: 0,
                        mt: 0.2,
                    }}
                />
                <Box>
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 700,
                            fontSize: "1rem",
                            color: "#fff",
                            mb: 0.5,
                        }}
                    >
                        {t("landing.hero.demoSuccessTitle")}
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "'Inter', sans-serif",
                            fontSize: "0.88rem",
                            color: "rgba(255,255,255,0.7)",
                            lineHeight: 1.6,
                        }}
                    >
                        {t("landing.hero.demoSuccessBody")}
                    </Typography>
                </Box>
            </Box>
        ) : (
            <Box sx={{ mb: 3, maxWidth: "520px", mx: centered ? "auto" : 0 }}>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                >
                    <TextField
                        type="email"
                        value={demoEmail}
                        onChange={(e) => {
                            setDemoEmail(e.target.value);
                            if (demoError) setDemoError("");
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") handleDemoRequest();
                        }}
                        placeholder={t("landing.hero.demoPlaceholder")}
                        fullWidth
                        error={!!demoError}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                background: "rgba(255,255,255,0.06)",
                                borderRadius: "12px",
                                color: "#fff",
                                fontFamily: "'Inter', sans-serif",
                                "& fieldset": {
                                    borderColor: "rgba(255,255,255,0.22)",
                                },
                                "&:hover fieldset": {
                                    borderColor: "rgba(255,255,255,0.4)",
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: colors.mainSecondary,
                                },
                            },
                            "& input": { padding: "14px 16px" },
                            "& input::placeholder": {
                                color: "rgba(255,255,255,0.5)",
                                opacity: 1,
                            },
                        }}
                    />
                    <GoldButton
                        endIcon={
                            demoSubmitting ? undefined : <ArrowForwardIcon />
                        }
                        disabled={demoSubmitting || !demoEmailValid}
                        onClick={handleDemoRequest}
                        sx={{
                            flexShrink: 0,
                            whiteSpace: "nowrap",
                            color: "#fff",
                            "&.Mui-disabled": {
                                color: "rgba(255,255,255,0.55)",
                            },
                        }}
                    >
                        {demoSubmitting ? (
                            <CircularProgress
                                size={20}
                                sx={{ color: "#fff" }}
                            />
                        ) : (
                            t("landing.hero.demoButton")
                        )}
                    </GoldButton>
                </Stack>
                {demoError && (
                    <Typography
                        sx={{
                            fontFamily: "'Inter', sans-serif",
                            fontSize: "0.78rem",
                            color: "#ff9b9b",
                            mt: 1,
                            textAlign: centered ? "center" : "left",
                        }}
                    >
                        {demoError}
                    </Typography>
                )}
            </Box>
        );

    return (
        <>
            <SEO
                title={t("landing.seo.title")}
                description={t("landing.seo.description")}
            />
            <PageWrapper>
                <BusinessNavbar />
                {/* ── Hero ── */}
                <Hero>
                    <GridOverlay />
                    <Orb
                        sx={{
                            top: "-120px",
                            right: "-60px",
                            width: 420,
                            height: 420,
                            background:
                                "radial-gradient(circle, rgba(255,176,0,0.30) 0%, transparent 70%)",
                            animation: `${floatYSlow} 9s ease-in-out infinite`,
                        }}
                    />
                    <Orb
                        sx={{
                            bottom: "-140px",
                            left: "-80px",
                            width: 460,
                            height: 460,
                            background:
                                "radial-gradient(circle, rgba(30,193,149,0.24) 0%, transparent 70%)",
                            animation: `${floatY} 11s ease-in-out infinite`,
                        }}
                    />
                    <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
                        <Grid container spacing={{ xs: 6, md: 5 }} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <Reveal>
                                    <Badge>
                                        <AutoAwesomeIcon sx={{ fontSize: "0.9rem" }} />
                                        {t("landing.hero.badge")}
                                    </Badge>
                                </Reveal>
                                <Reveal delay={0.08}>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontSize: "clamp(2.1rem, 5.4vw, 3.6rem)",
                                            fontWeight: 800,
                                            color: "#fff",
                                            lineHeight: 1.08,
                                            letterSpacing: "-0.03em",
                                            mt: 2.5,
                                            mb: 2.5,
                                        }}
                                    >
                                        {t("landing.hero.titleLine1")}
                                        <br />
                                        {t("landing.hero.titleLine2")}{" "}
                                        <GoldSpan>
                                            {t("landing.hero.titleAccent")}
                                        </GoldSpan>
                                        .
                                    </Typography>
                                </Reveal>
                                <Reveal delay={0.16}>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Inter', sans-serif",
                                            fontSize: "clamp(1rem, 1.8vw, 1.15rem)",
                                            color: "rgba(255,255,255,0.66)",
                                            lineHeight: 1.74,
                                            maxWidth: "540px",
                                            mb: 4,
                                        }}
                                    >
                                        {t("landing.hero.subtitle")}
                                    </Typography>
                                </Reveal>
                                <Reveal delay={0.2}>
                                    <Stack
                                        direction={{ xs: "column", sm: "row" }}
                                        spacing={1.6}
                                        useFlexGap
                                        sx={{
                                            mb: 2,
                                            alignItems: {
                                                xs: "stretch",
                                                sm: "center",
                                            },
                                        }}
                                    >
                                        <GoldButton
                                            endIcon={<ArrowForwardIcon />}
                                            onClick={() =>
                                                navigate("/collect/sign-up")
                                            }
                                            sx={{ whiteSpace: "nowrap" }}
                                        >
                                            {t("landing.hero.ctaPrimaryTrial")}
                                        </GoldButton>
                                        <Button
                                            onClick={() =>
                                                document
                                                    .getElementById("pricing")
                                                    ?.scrollIntoView({
                                                        behavior: "smooth",
                                                        block: "start",
                                                    })
                                            }
                                            sx={{
                                                fontFamily:
                                                    "'Montserrat', sans-serif",
                                                fontWeight: 700,
                                                textTransform: "none",
                                                fontSize: "1rem",
                                                color: "#fff",
                                                borderRadius: "12px",
                                                padding: "15px 28px",
                                                border: "1px solid rgba(255,255,255,0.35)",
                                                background: "rgba(255,255,255,0.04)",
                                                whiteSpace: "nowrap",
                                                "&:hover": {
                                                    background:
                                                        "rgba(255,255,255,0.1)",
                                                    borderColor:
                                                        "rgba(255,255,255,0.6)",
                                                },
                                            }}
                                        >
                                            {t("landing.hero.ctaWalkthrough")}
                                        </Button>
                                    </Stack>
                                </Reveal>
                                <Reveal delay={0.26}>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Inter', sans-serif",
                                            fontSize: "0.85rem",
                                            color: "rgba(255,255,255,0.78)",
                                            lineHeight: 1.6,
                                            mb: 3,
                                        }}
                                    >
                                        {t("landing.hero.trustMicro")}
                                    </Typography>
                                </Reveal>
                                <Reveal delay={0.32}>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontFamily: "'Inter', sans-serif",
                                                fontSize: "0.84rem",
                                                color: "rgba(255,255,255,0.72)",
                                                lineHeight: 1.62,
                                                mb: 1.6,
                                                maxWidth: "520px",
                                            }}
                                        >
                                            {t("landing.hero.setupLine")}
                                        </Typography>
                                        <Stack
                                            direction="row"
                                            flexWrap="wrap"
                                            useFlexGap
                                            sx={{ gap: 0.8, alignItems: "center" }}
                                        >
                                            {setupItems.map((step, i) => (
                                                <React.Fragment key={step}>
                                                    <Box
                                                        sx={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            px: 1.3,
                                                            py: 0.6,
                                                            borderRadius: "999px",
                                                            background:
                                                                "rgba(255,255,255,0.08)",
                                                            border: "1px solid rgba(255,255,255,0.16)",
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontFamily:
                                                                    "'Inter', sans-serif",
                                                                fontSize: "0.74rem",
                                                                fontWeight: 600,
                                                                color: "rgba(255,255,255,0.88)",
                                                            }}
                                                        >
                                                            {i + 1}. {step}
                                                        </Typography>
                                                    </Box>
                                                    {i <
                                                        setupItems.length - 1 && (
                                                            <ArrowForwardIcon
                                                                sx={{
                                                                    fontSize: "0.85rem",
                                                                    color: "rgba(255,255,255,0.4)",
                                                                }}
                                                            />
                                                        )}
                                                </React.Fragment>
                                            ))}
                                        </Stack>
                                    </Box>
                                </Reveal>
                            </Grid>

                            {/* Hero product mockup */}
                            <Grid item xs={12} md={6}>
                                <Reveal delay={0.2} y={40}>
                                    <Box
                                        sx={{
                                            position: "relative",
                                            display: "flex",
                                            justifyContent: "center",
                                            mt: { xs: 2, md: 0 },
                                        }}
                                    >
                                        <MockCard
                                            sx={{
                                                animation: `${floatY} 7s ease-in-out infinite`,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    mb: 2,
                                                }}
                                            >
                                                <Box>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: "'Montserrat', sans-serif",
                                                            fontWeight: 800,
                                                            fontSize: "0.9rem",
                                                            color: colors.primary,
                                                        }}
                                                    >
                                                        {t("landing.dash.title")}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: "'Inter', sans-serif",
                                                            fontSize: "0.68rem",
                                                            color: "#9aa3b2",
                                                        }}
                                                    >
                                                        {t("landing.dash.live")}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.6,
                                                        background: "rgba(30,193,149,0.12)",
                                                        borderRadius: "8px",
                                                        px: 1,
                                                        py: 0.4,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 7,
                                                            height: 7,
                                                            borderRadius: "50%",
                                                            background: colors.mainSecondary,
                                                        }}
                                                    />
                                                    <Typography
                                                        sx={{
                                                            fontFamily: "'Inter', sans-serif",
                                                            fontSize: "0.64rem",
                                                            fontWeight: 700,
                                                            color: colors.mainSecondary,
                                                        }}
                                                    >
                                                        {t("landing.dash.onTrack")}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* KPI tiles */}
                                            <Grid container spacing={1.2} sx={{ mb: 2 }}>
                                                {[
                                                    {
                                                        label: t("landing.dash.kpiRecovered"),
                                                        node: (
                                                            <StatCounter
                                                                to={48200}
                                                                prefix="$"
                                                            />
                                                        ),
                                                        color: colors.mainSecondary,
                                                    },
                                                    {
                                                        label: t("landing.dash.kpiOutstanding"),
                                                        node: (
                                                            <StatCounter
                                                                to={17500}
                                                                prefix="$"
                                                            />
                                                        ),
                                                        color: "#ffb300",
                                                    },
                                                    {
                                                        label: t("landing.dash.kpiPromises"),
                                                        node: <StatCounter to={12} />,
                                                        color: "#143AA2",
                                                    },
                                                ].map((k) => (
                                                    <Grid item xs={4} key={k.label}>
                                                        <Box
                                                            sx={{
                                                                background: "#f4f7fe",
                                                                borderRadius: "12px",
                                                                p: 1.4,
                                                                border: "1px solid rgba(4,13,69,0.05)",
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontFamily:
                                                                        "'Montserrat', sans-serif",
                                                                    fontWeight: 800,
                                                                    fontSize: "1.02rem",
                                                                    color: k.color,
                                                                    lineHeight: 1.1,
                                                                }}
                                                            >
                                                                {k.node}
                                                            </Typography>
                                                            <Typography
                                                                sx={{
                                                                    fontFamily: "'Inter', sans-serif",
                                                                    fontSize: "0.62rem",
                                                                    color: "#8a93a3",
                                                                    mt: 0.3,
                                                                }}
                                                            >
                                                                {k.label}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                ))}
                                            </Grid>

                                            {/* Queue rows */}
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Montserrat', sans-serif",
                                                    fontWeight: 700,
                                                    fontSize: "0.66rem",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.08em",
                                                    color: "#9aa3b2",
                                                    mb: 1,
                                                }}
                                            >
                                                {t("landing.dash.queueLabel")}
                                            </Typography>
                                            <Stack spacing={1}>
                                                {QUEUE_ROWS.map((r) => (
                                                    <Box
                                                        key={r.name}
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1.2,
                                                            background: "#fff",
                                                            border: "1px solid rgba(4,13,69,0.06)",
                                                            borderRadius: "10px",
                                                            p: 1.1,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 34,
                                                                height: 34,
                                                                borderRadius: "9px",
                                                                background: `${r.tone}1a`,
                                                                color: r.tone,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                fontFamily:
                                                                    "'Montserrat', sans-serif",
                                                                fontWeight: 800,
                                                                fontSize: "0.66rem",
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            {r.days}
                                                        </Box>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography
                                                                noWrap
                                                                sx={{
                                                                    fontFamily:
                                                                        "'Inter', sans-serif",
                                                                    fontWeight: 600,
                                                                    fontSize: "0.78rem",
                                                                    color: colors.primary,
                                                                }}
                                                            >
                                                                {r.name}
                                                            </Typography>
                                                            <Box
                                                                sx={{
                                                                    height: 5,
                                                                    borderRadius: "100px",
                                                                    background: "#eef1f7",
                                                                    mt: 0.5,
                                                                    overflow: "hidden",
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        height: "100%",
                                                                        width: `${r.pct}%`,
                                                                        borderRadius: "100px",
                                                                        background: r.tone,
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                        <Typography
                                                            sx={{
                                                                fontFamily:
                                                                    "'Montserrat', sans-serif",
                                                                fontWeight: 800,
                                                                fontSize: "0.8rem",
                                                                color: colors.primary,
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            {r.amount}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </MockCard>

                                        {/* Floating notification cards */}
                                        <FloatChip
                                            sx={{
                                                top: { xs: 4, sm: -18 },
                                                right: { xs: 4, sm: -10 },
                                                animation: `${floatY} 5.5s ease-in-out infinite`,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 30,
                                                    height: 30,
                                                    borderRadius: "8px",
                                                    background: "rgba(30,193,149,0.14)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <CheckCircleIcon
                                                    sx={{
                                                        fontSize: "1rem",
                                                        color: colors.mainSecondary,
                                                    }}
                                                />
                                            </Box>
                                            <Box>
                                                <Typography
                                                    sx={{
                                                        fontFamily: "'Montserrat', sans-serif",
                                                        fontWeight: 700,
                                                        fontSize: "0.72rem",
                                                        color: colors.primary,
                                                    }}
                                                >
                                                    {t("landing.dash.chipPaymentTitle")}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontFamily: "'Inter', sans-serif",
                                                        fontSize: "0.64rem",
                                                        color: colors.mainSecondary,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {t("landing.dash.chipPaymentSub")}
                                                </Typography>
                                            </Box>
                                        </FloatChip>

                                        <FloatChip
                                            sx={{
                                                bottom: { xs: 4, sm: -16 },
                                                left: { xs: 4, sm: -14 },
                                                animation: `${floatYSlow} 6.5s ease-in-out infinite`,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 30,
                                                    height: 30,
                                                    borderRadius: "8px",
                                                    background: "rgba(167,139,250,0.16)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <PsychologyIcon
                                                    sx={{ fontSize: "1rem", color: "#a78bfa" }}
                                                />
                                            </Box>
                                            <Box>
                                                <Typography
                                                    sx={{
                                                        fontFamily: "'Montserrat', sans-serif",
                                                        fontWeight: 700,
                                                        fontSize: "0.72rem",
                                                        color: colors.primary,
                                                    }}
                                                >
                                                    {t("landing.dash.chipSmsTitle")}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontFamily: "'Inter', sans-serif",
                                                        fontSize: "0.64rem",
                                                        color: "#a78bfa",
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {t("landing.dash.chipSmsSub")}
                                                </Typography>
                                            </Box>
                                        </FloatChip>
                                    </Box>
                                </Reveal>
                            </Grid>
                        </Grid>
                    </Container>
                </Hero>

                {/* ── Trust marquee ── */}
                <Box
                    sx={{
                        background: colors.primary,
                        py: 1.8,
                        overflow: "hidden",
                        position: "relative",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            width: "max-content",
                            animation: `${marquee} 28s linear infinite`,
                            "&:hover": { animationPlayState: "paused" },
                        }}
                    >
                        {[...trustItems, ...trustItems].map((item, i) => (
                            <Box
                                key={i}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    px: 3.5,
                                    flexShrink: 0,
                                }}
                            >
                                <CheckIcon
                                    sx={{ fontSize: "0.9rem", color: "#ffcc00" }}
                                />
                                <Typography
                                    sx={{
                                        fontFamily: "'Inter', sans-serif",
                                        fontSize: "0.82rem",
                                        fontWeight: 500,
                                        color: "rgba(255,255,255,0.78)",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {item}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ── Features ── */}
                <Section id="features" tone="tint">
                    <Container maxWidth="lg">
                        <Reveal>
                            <SectionLabel>{t("landing.features.label")}</SectionLabel>
                            <SectionTitle>
                                {t("landing.features.title")}
                            </SectionTitle>
                            <SectionSub>
                                {t("landing.features.subtitle")}
                            </SectionSub>
                        </Reveal>
                        <Grid container spacing={3}>
                            {FEATURE_VISUALS.map((f, i) => (
                                <Grid item xs={12} sm={6} md={4} key={i}>
                                    <Reveal delay={(i % 3) * 0.08}>
                                        <FeatureCard accent={f.accent}>
                                            <IconWrap accent={f.accent}>{f.icon}</IconWrap>
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Montserrat', sans-serif",
                                                    fontWeight: 700,
                                                    fontSize: "1.04rem",
                                                    color: colors.primary,
                                                    mb: 1,
                                                }}
                                            >
                                                {featureItems[i]?.title}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Inter', sans-serif",
                                                    fontSize: "0.9rem",
                                                    color: "#5b6472",
                                                    lineHeight: 1.72,
                                                }}
                                            >
                                                {featureItems[i]?.desc}
                                            </Typography>
                                        </FeatureCard>
                                    </Reveal>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </Section>

                {/* ── Data control / trust ── */}
                <Section tone="light">
                    <Container maxWidth="lg">
                        <Reveal>
                            <SectionLabel>
                                {t("landing.dataControl.label")}
                            </SectionLabel>
                            <SectionTitle>
                                {t("landing.dataControl.title")}
                            </SectionTitle>
                            <SectionSub>
                                {t("landing.dataControl.subtitle")}
                            </SectionSub>
                        </Reveal>
                        <Grid container spacing={3}>
                            {DATACONTROL_VISUALS.map((d, i) => (
                                <Grid item xs={12} sm={6} key={i}>
                                    <Reveal delay={(i % 2) * 0.08}>
                                        <FeatureCard accent={d.accent}>
                                            <IconWrap accent={d.accent}>
                                                {d.icon}
                                            </IconWrap>
                                            <Typography
                                                sx={{
                                                    fontFamily:
                                                        "'Montserrat', sans-serif",
                                                    fontWeight: 700,
                                                    fontSize: "1.04rem",
                                                    color: colors.primary,
                                                    mb: 1,
                                                }}
                                            >
                                                {dataControlPoints[i]?.title}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily:
                                                        "'Inter', sans-serif",
                                                    fontSize: "0.9rem",
                                                    color: "#5b6472",
                                                    lineHeight: 1.72,
                                                }}
                                            >
                                                {dataControlPoints[i]?.desc}
                                            </Typography>
                                        </FeatureCard>
                                    </Reveal>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </Section>

                {/* ── Audience / who it's for ── */}
                <Section tone="tint">
                    <Container maxWidth="lg">
                        <Reveal>
                            <SectionLabel>
                                {t("landing.audience.label")}
                            </SectionLabel>
                            <SectionTitle>
                                {t("landing.audience.title")}
                            </SectionTitle>
                            <SectionSub>
                                {t("landing.audience.subtitle")}
                            </SectionSub>
                        </Reveal>
                        <Grid container spacing={3}>
                            {AUDIENCE_VISUALS.map((a, i) => (
                                <Grid item xs={12} sm={6} md={4} key={i}>
                                    <Reveal delay={(i % 3) * 0.08}>
                                        <FeatureCard accent={a.accent}>
                                            <IconWrap accent={a.accent}>
                                                {a.icon}
                                            </IconWrap>
                                            <Typography
                                                sx={{
                                                    fontFamily:
                                                        "'Montserrat', sans-serif",
                                                    fontWeight: 700,
                                                    fontSize: "1.04rem",
                                                    color: colors.primary,
                                                    mb: 1,
                                                }}
                                            >
                                                {audienceItems[i]?.title}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily:
                                                        "'Inter', sans-serif",
                                                    fontSize: "0.9rem",
                                                    color: "#5b6472",
                                                    lineHeight: 1.72,
                                                }}
                                            >
                                                {audienceItems[i]?.desc}
                                            </Typography>
                                        </FeatureCard>
                                    </Reveal>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </Section>

                {/* ── How it works ── */}
                <Section tone="light">
                    <Container maxWidth="lg">
                        <Reveal>
                            <SectionLabel>{t("landing.steps.label")}</SectionLabel>
                            <SectionTitle>{t("landing.steps.title")}</SectionTitle>
                            <SectionSub>
                                {t("landing.steps.subtitle")}
                            </SectionSub>
                        </Reveal>
                        <Grid container spacing={4}>
                            {STEP_ICONS.map((stepIcon, i) => (
                                <Grid
                                    item
                                    xs={12}
                                    md={4}
                                    key={i}
                                    sx={{ position: "relative" }}
                                >
                                    {i < STEP_ICONS.length - 1 && <StepConnector />}
                                    <Reveal delay={i * 0.1}>
                                        <Box sx={{ textAlign: "center", px: 1 }}>
                                            <Box
                                                sx={{
                                                    position: "relative",
                                                    width: 70,
                                                    height: 70,
                                                    mx: "auto",
                                                    mb: 2.5,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        inset: 0,
                                                        borderRadius: "20px",
                                                        background:
                                                            "linear-gradient(135deg, #1ec195 0%, #143AA2 100%)",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        color: "#fff",
                                                        boxShadow:
                                                            "0 14px 30px rgba(20,58,162,0.30)",
                                                        zIndex: 1,
                                                    }}
                                                >
                                                    {React.cloneElement(stepIcon, {
                                                        sx: { fontSize: 30 },
                                                    })}
                                                </Box>
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        top: -8,
                                                        right: -8,
                                                        width: 28,
                                                        height: 28,
                                                        borderRadius: "50%",
                                                        background: "#ffcc00",
                                                        color: colors.primary,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontFamily:
                                                            "'Montserrat', sans-serif",
                                                        fontWeight: 800,
                                                        fontSize: "0.8rem",
                                                        zIndex: 2,
                                                        boxShadow:
                                                            "0 4px 10px rgba(255,176,0,0.4)",
                                                    }}
                                                >
                                                    {i + 1}
                                                </Box>
                                            </Box>
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Montserrat', sans-serif",
                                                    fontWeight: 800,
                                                    fontSize: "1.18rem",
                                                    color: colors.primary,
                                                    mb: 1,
                                                }}
                                            >
                                                {stepItems[i]?.title}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Inter', sans-serif",
                                                    fontSize: "0.93rem",
                                                    color: "#5b6472",
                                                    lineHeight: 1.74,
                                                    maxWidth: "320px",
                                                    mx: "auto",
                                                }}
                                            >
                                                {stepItems[i]?.desc}
                                            </Typography>
                                        </Box>
                                    </Reveal>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </Section>

                {/* ── One-invoice demo ── */}
                <Section id="demo" tone="tint">
                    <Container maxWidth="md">
                        <Reveal>
                            <SectionLabel>{t("landing.demo.label")}</SectionLabel>
                            <SectionTitle>{t("landing.demo.title")}</SectionTitle>
                            <SectionSub>{t("landing.demo.subtitle")}</SectionSub>
                        </Reveal>

                        <Reveal delay={0.05}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexWrap: "wrap",
                                    gap: 1.5,
                                    background:
                                        "linear-gradient(135deg, #070e29 0%, #16245c 100%)",
                                    borderRadius: "16px",
                                    px: { xs: 2.5, md: 3.5 },
                                    py: { xs: 2, md: 2.4 },
                                    mb: 4,
                                    boxShadow: "0 18px 40px -22px rgba(4,13,69,0.6)",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.6,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 42,
                                            height: 42,
                                            borderRadius: "12px",
                                            background: "rgba(255,204,0,0.16)",
                                            border: "1px solid rgba(255,204,0,0.35)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "#ffcc00",
                                        }}
                                    >
                                        <MarkEmailReadOutlinedIcon
                                            sx={{ fontSize: 22 }}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontFamily:
                                                    "'Montserrat', sans-serif",
                                                fontWeight: 700,
                                                fontSize: "1rem",
                                                color: "#fff",
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {t("landing.demo.customer")}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: "'Inter', sans-serif",
                                                fontSize: "0.82rem",
                                                color: "rgba(255,255,255,0.6)",
                                            }}
                                        >
                                            {t("landing.demo.invoice")}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily: "'Montserrat', sans-serif",
                                        fontWeight: 800,
                                        fontSize: "1.35rem",
                                        color: "#ffcc00",
                                    }}
                                >
                                    {t("landing.demo.amount")}
                                </Typography>
                            </Box>
                        </Reveal>

                        <Box sx={{ position: "relative" }}>
                            {DEMO_VISUALS.map((v, i) => (
                                <Reveal delay={i * 0.06} key={i}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: { xs: 1.8, md: 2.4 },
                                            pb:
                                                i < DEMO_VISUALS.length - 1
                                                    ? 3
                                                    : 0,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 46,
                                                    height: 46,
                                                    borderRadius: "13px",
                                                    background: `${v.accent}1a`,
                                                    border: `1px solid ${v.accent}40`,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: v.accent,
                                                }}
                                            >
                                                {v.icon}
                                            </Box>
                                            {i < DEMO_VISUALS.length - 1 && (
                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        width: "2px",
                                                        minHeight: 26,
                                                        mt: 1,
                                                        background:
                                                            "linear-gradient(180deg, rgba(4,13,69,0.18), rgba(4,13,69,0.04))",
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Box
                                            sx={{
                                                background: "#fff",
                                                border: "1px solid rgba(4,13,69,0.07)",
                                                borderRadius: "14px",
                                                p: { xs: 2, md: 2.4 },
                                                flex: 1,
                                                boxShadow:
                                                    "0 1px 2px rgba(4,13,69,0.04)",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    px: 1.2,
                                                    py: 0.3,
                                                    mb: 1,
                                                    borderRadius: "999px",
                                                    background: `${v.accent}14`,
                                                    color: v.accent,
                                                    fontFamily:
                                                        "'Montserrat', sans-serif",
                                                    fontWeight: 700,
                                                    fontSize: "0.68rem",
                                                    letterSpacing: "0.06em",
                                                    textTransform: "uppercase",
                                                }}
                                            >
                                                {demoSteps[i]?.tag}
                                            </Box>
                                            <Typography
                                                sx={{
                                                    fontFamily:
                                                        "'Montserrat', sans-serif",
                                                    fontWeight: 700,
                                                    fontSize: "1.02rem",
                                                    color: colors.primary,
                                                    mb: 0.6,
                                                }}
                                            >
                                                {demoSteps[i]?.title}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily:
                                                        "'Inter', sans-serif",
                                                    fontSize: "0.9rem",
                                                    color: "#5b6472",
                                                    lineHeight: 1.7,
                                                }}
                                            >
                                                {demoSteps[i]?.desc}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Reveal>
                            ))}
                        </Box>

                        <Typography
                            sx={{
                                fontFamily: "'Inter', sans-serif",
                                fontSize: "0.8rem",
                                color: "#8a93a3",
                                textAlign: "center",
                                mt: 3.5,
                            }}
                        >
                            {t("landing.demo.note")}
                        </Typography>
                    </Container>
                </Section>

                {/* ── Product tour ── */}
                <Section id="tour" tone="dark">
                    <Container maxWidth="lg">
                        <Reveal>
                            <SectionLabel ondark={1}>
                                {t("landing.tour.label")}
                            </SectionLabel>
                        </Reveal>
                        <Reveal delay={0.06}>
                            <SectionTitle ondark={1}>
                                {t("landing.tour.title")}
                            </SectionTitle>
                        </Reveal>
                        <Reveal delay={0.12}>
                            <SectionSub ondark={1}>
                                {t("landing.tour.subtitle")}
                            </SectionSub>
                        </Reveal>

                        <Grid
                            container
                            spacing={{ xs: 4, md: 5 }}
                            alignItems="center"
                        >
                            {/* Clickable feature list */}
                            <Grid item xs={12} md={5}>
                                <Stack spacing={1.6}>
                                    {tourItems.map((item, i) => {
                                        const active = activeTour === i;
                                        const accent =
                                            TOUR_SHOTS[i]?.accent ??
                                            colors.mainSecondary;
                                        return (
                                            <Reveal key={i} delay={0.05 * i}>
                                                <Box
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-pressed={active}
                                                    onClick={() =>
                                                        setActiveTour(i)
                                                    }
                                                    onKeyDown={(e) => {
                                                        if (
                                                            e.key === "Enter" ||
                                                            e.key === " "
                                                        ) {
                                                            e.preventDefault();
                                                            setActiveTour(i);
                                                        }
                                                    }}
                                                    sx={{
                                                        cursor: "pointer",
                                                        borderRadius: "16px",
                                                        border: active
                                                            ? `1px solid ${accent}66`
                                                            : "1px solid rgba(255,255,255,0.09)",
                                                        background: active
                                                            ? "rgba(255,255,255,0.06)"
                                                            : "transparent",
                                                        p: 2.2,
                                                        display: "flex",
                                                        gap: 1.6,
                                                        transition:
                                                            "border-color 0.25s ease, background 0.25s ease",
                                                        "&:hover": {
                                                            background:
                                                                "rgba(255,255,255,0.05)",
                                                            borderColor: `${accent}55`,
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 42,
                                                            height: 42,
                                                            borderRadius: "11px",
                                                            flexShrink: 0,
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                            background: `${accent}1f`,
                                                            border: `1px solid ${accent}40`,
                                                            color: accent,
                                                        }}
                                                    >
                                                        {TOUR_SHOTS[i]?.icon}
                                                    </Box>
                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                fontFamily:
                                                                    "'Montserrat', sans-serif",
                                                                fontWeight: 700,
                                                                fontSize:
                                                                    "1.02rem",
                                                                color: "#fff",
                                                                mb: active
                                                                    ? 0.6
                                                                    : 0,
                                                            }}
                                                        >
                                                            {item.title}
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                maxHeight: active
                                                                    ? "160px"
                                                                    : 0,
                                                                opacity: active
                                                                    ? 1
                                                                    : 0,
                                                                overflow:
                                                                    "hidden",
                                                                transition:
                                                                    "max-height 0.35s ease, opacity 0.35s ease",
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontFamily:
                                                                        "'Inter', sans-serif",
                                                                    fontSize:
                                                                        "0.9rem",
                                                                    color: "rgba(255,255,255,0.66)",
                                                                    lineHeight: 1.66,
                                                                }}
                                                            >
                                                                {item.desc}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Reveal>
                                        );
                                    })}
                                </Stack>
                            </Grid>

                            {/* Screenshot in a browser frame */}
                            <Grid item xs={12} md={7}>
                                <Reveal delay={0.15} y={36}>
                                    <BrowserFrame>
                                        <BrowserBar>
                                            <BrowserDot c="#ff5f57" />
                                            <BrowserDot c="#febc2e" />
                                            <BrowserDot c="#28c840" />
                                            <Box
                                                sx={{
                                                    ml: 1.5,
                                                    flex: 1,
                                                    height: 22,
                                                    borderRadius: "6px",
                                                    background:
                                                        "rgba(255,255,255,0.06)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    px: 1.5,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontFamily:
                                                            "'Inter', sans-serif",
                                                        fontSize: "0.72rem",
                                                        color: "rgba(255,255,255,0.4)",
                                                    }}
                                                >
                                                    fundsyfi.com/collect/
                                                </Typography>
                                            </Box>
                                        </BrowserBar>
                                        <Box
                                            sx={{
                                                position: "relative",
                                                aspectRatio: "16 / 10",
                                                background:
                                                    "linear-gradient(160deg,#0d1636,#16245c)",
                                            }}
                                        >
                                            {tourItems.map((item, i) => (
                                                <Box
                                                    component="img"
                                                    key={i}
                                                    src={TOUR_SHOTS[i]?.img}
                                                    alt={item.title}
                                                    loading="lazy"
                                                    onError={(e) => {
                                                        (
                                                            e.currentTarget as HTMLImageElement
                                                        ).style.visibility =
                                                            "hidden";
                                                    }}
                                                    sx={{
                                                        position: "absolute",
                                                        inset: 0,
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "contain",
                                                        objectPosition:
                                                            "center",
                                                        p: 1.5,
                                                        opacity:
                                                            activeTour === i
                                                                ? 1
                                                                : 0,
                                                        transition:
                                                            "opacity 0.5s ease",
                                                        pointerEvents:
                                                            activeTour === i
                                                                ? "auto"
                                                                : "none",
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </BrowserFrame>
                                </Reveal>
                            </Grid>
                        </Grid>
                    </Container>
                </Section>

                {/* ── AI reply showcase ── */}
                <Section tone="tint">
                    <Container maxWidth="lg">
                        <Reveal>
                            <SectionLabel>{t("landing.ai.label")}</SectionLabel>
                            <SectionTitle>{t("landing.ai.title")}</SectionTitle>
                            <SectionSub sx={{ mb: "20px" }}>
                                {t("landing.ai.body")}
                            </SectionSub>
                        </Reveal>
                        <Stack
                            direction="row"
                            spacing={{ xs: 1, sm: 2.5 }}
                            rowGap={1.2}
                            flexWrap="wrap"
                            justifyContent="center"
                            useFlexGap
                            sx={{
                                maxWidth: 820,
                                mx: "auto",
                                mb: { xs: 4, md: 6 },
                            }}
                        >
                            {aiPoints.map((p) => (
                                <Box
                                    key={p}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <CheckCircleIcon
                                        sx={{
                                            fontSize: "1.1rem",
                                            color: colors.mainSecondary,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontFamily: "'Inter', sans-serif",
                                            fontSize: "0.9rem",
                                            color: "#374151",
                                            fontWeight: 600,
                                        }}
                                    >
                                        {p}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                        <Grid
                            container
                            spacing={{ xs: 3, md: 4 }}
                            justifyContent="center"
                            alignItems="stretch"
                        >
                            <Grid item xs={12} sm={6} md={5}>
                                <Reveal delay={0.12} y={36}>
                                    <Box
                                        sx={{
                                            background: "#fff",
                                            borderRadius: "20px",
                                            border: "1px solid rgba(4,13,69,0.07)",
                                            boxShadow: "0 24px 60px rgba(4,13,69,0.12)",
                                            p: 3,
                                            maxWidth: "440px",
                                            mx: "auto",
                                            animation: `${bounceCard} 3.2s ease-in-out infinite`,
                                            "@media (prefers-reduced-motion: reduce)": {
                                                animation: "none",
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.2,
                                                pb: 2,
                                                mb: 2,
                                                borderBottom: "1px solid #eef1f7",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 38,
                                                    height: 38,
                                                    borderRadius: "50%",
                                                    background:
                                                        "linear-gradient(135deg, #1ec195, #143AA2)",
                                                    color: "#fff",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontFamily: "'Montserrat', sans-serif",
                                                    fontWeight: 800,
                                                    fontSize: "0.85rem",
                                                }}
                                            >
                                                NS
                                            </Box>
                                            <Box>
                                                <Typography
                                                    sx={{
                                                        fontFamily: "'Montserrat', sans-serif",
                                                        fontWeight: 700,
                                                        fontSize: "0.85rem",
                                                        color: colors.primary,
                                                    }}
                                                >
                                                    {t("landing.ai.convName")}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontFamily: "'Inter', sans-serif",
                                                        fontSize: "0.68rem",
                                                        color: "#9aa3b2",
                                                    }}
                                                >
                                                    {t("landing.ai.convChannel")}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Outbound */}
                                        <Box
                                            sx={{
                                                background: "#eef2fb",
                                                borderRadius: "14px 14px 14px 4px",
                                                p: 1.5,
                                                mb: 1.5,
                                                maxWidth: "82%",
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Inter', sans-serif",
                                                    fontSize: "0.84rem",
                                                    color: "#374151",
                                                    lineHeight: 1.6,
                                                }}
                                            >
                                                {t("landing.ai.outbound")}
                                            </Typography>
                                        </Box>
                                        {/* Inbound */}
                                        <Box
                                            sx={{
                                                background:
                                                    "linear-gradient(135deg, #1ec195, #17a982)",
                                                borderRadius: "14px 14px 4px 14px",
                                                p: 1.5,
                                                mb: 2,
                                                ml: "auto",
                                                maxWidth: "82%",
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Inter', sans-serif",
                                                    fontSize: "0.84rem",
                                                    color: "#fff",
                                                    lineHeight: 1.6,
                                                }}
                                            >
                                                {t("landing.ai.inbound")}
                                            </Typography>
                                        </Box>

                                        {/* AI classification */}
                                        <Box
                                            sx={{
                                                background: "rgba(167,139,250,0.08)",
                                                border: "1px dashed rgba(167,139,250,0.4)",
                                                borderRadius: "12px",
                                                p: 1.6,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.8,
                                                    mb: 0.8,
                                                }}
                                            >
                                                <AutoAwesomeIcon
                                                    sx={{
                                                        fontSize: "0.9rem",
                                                        color: "#a78bfa",
                                                    }}
                                                />
                                                <Typography
                                                    sx={{
                                                        fontFamily: "'Montserrat', sans-serif",
                                                        fontWeight: 700,
                                                        fontSize: "0.66rem",
                                                        textTransform: "uppercase",
                                                        letterSpacing: "0.08em",
                                                        color: "#a78bfa",
                                                    }}
                                                >
                                                    {t("landing.ai.understoodAs")}
                                                </Typography>
                                            </Box>
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                flexWrap="wrap"
                                                useFlexGap
                                            >
                                                <Box
                                                    sx={{
                                                        background: "rgba(30,193,149,0.14)",
                                                        color: "#0f9b76",
                                                        borderRadius: "100px",
                                                        px: 1.4,
                                                        py: 0.5,
                                                        fontFamily: "'Inter', sans-serif",
                                                        fontWeight: 700,
                                                        fontSize: "0.72rem",
                                                    }}
                                                >
                                                    {t("landing.ai.chip1")}
                                                </Box>
                                                <Box
                                                    sx={{
                                                        background: "rgba(20,58,162,0.1)",
                                                        color: "#143AA2",
                                                        borderRadius: "100px",
                                                        px: 1.4,
                                                        py: 0.5,
                                                        fontFamily: "'Inter', sans-serif",
                                                        fontWeight: 700,
                                                        fontSize: "0.72rem",
                                                    }}
                                                >
                                                    {t("landing.ai.chip2")}
                                                </Box>
                                            </Stack>
                                        </Box>
                                    </Box>
                                </Reveal>
                            </Grid>

                            {/* ── Email reply card ── */}
                            <Grid item xs={12} sm={6} md={5}>
                                <Reveal delay={0.2} y={36}>
                                    <Box
                                        sx={{
                                            background: "#fff",
                                            borderRadius: "20px",
                                            border: "1px solid rgba(4,13,69,0.07)",
                                            boxShadow: "0 24px 60px rgba(4,13,69,0.12)",
                                            p: 3,
                                            maxWidth: "440px",
                                            mx: "auto",
                                            animation: `${bounceCard} 3.2s ease-in-out infinite`,
                                            animationDelay: "0.6s",
                                            "@media (prefers-reduced-motion: reduce)": {
                                                animation: "none",
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.2,
                                                pb: 2,
                                                mb: 2,
                                                borderBottom: "1px solid #eef1f7",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 38,
                                                    height: 38,
                                                    borderRadius: "11px",
                                                    background:
                                                        "linear-gradient(135deg, #143AA2, #040d45)",
                                                    color: "#fff",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <MarkEmailReadOutlinedIcon
                                                    sx={{ fontSize: "1.15rem" }}
                                                />
                                            </Box>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: "'Montserrat', sans-serif",
                                                        fontWeight: 700,
                                                        fontSize: "0.85rem",
                                                        color: colors.primary,
                                                    }}
                                                >
                                                    {t("landing.ai.emailName")}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontFamily: "'Inter', sans-serif",
                                                        fontSize: "0.68rem",
                                                        color: "#9aa3b2",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {t("landing.ai.emailChannel")}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Subject line */}
                                        <Typography
                                            sx={{
                                                fontFamily: "'Montserrat', sans-serif",
                                                fontWeight: 700,
                                                fontSize: "0.8rem",
                                                color: colors.primary,
                                                mb: 1.4,
                                            }}
                                        >
                                            {t("landing.ai.emailSubject")}
                                        </Typography>

                                        {/* Inbound email body */}
                                        <Box
                                            sx={{
                                                background: "#f7f9fc",
                                                border: "1px solid #eef1f7",
                                                borderRadius: "12px",
                                                p: 1.6,
                                                mb: 2,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Inter', sans-serif",
                                                    fontSize: "0.84rem",
                                                    color: "#374151",
                                                    lineHeight: 1.7,
                                                }}
                                            >
                                                {t("landing.ai.emailInbound")}
                                            </Typography>
                                        </Box>

                                        {/* AI classification */}
                                        <Box
                                            sx={{
                                                background: "rgba(167,139,250,0.08)",
                                                border: "1px dashed rgba(167,139,250,0.4)",
                                                borderRadius: "12px",
                                                p: 1.6,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.8,
                                                    mb: 0.8,
                                                }}
                                            >
                                                <AutoAwesomeIcon
                                                    sx={{
                                                        fontSize: "0.9rem",
                                                        color: "#a78bfa",
                                                    }}
                                                />
                                                <Typography
                                                    sx={{
                                                        fontFamily: "'Montserrat', sans-serif",
                                                        fontWeight: 700,
                                                        fontSize: "0.66rem",
                                                        textTransform: "uppercase",
                                                        letterSpacing: "0.08em",
                                                        color: "#a78bfa",
                                                    }}
                                                >
                                                    {t("landing.ai.understoodAs")}
                                                </Typography>
                                            </Box>
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                flexWrap="wrap"
                                                useFlexGap
                                            >
                                                <Box
                                                    sx={{
                                                        background: "rgba(239,68,68,0.12)",
                                                        color: "#dc2626",
                                                        borderRadius: "100px",
                                                        px: 1.4,
                                                        py: 0.5,
                                                        fontFamily: "'Inter', sans-serif",
                                                        fontWeight: 700,
                                                        fontSize: "0.72rem",
                                                    }}
                                                >
                                                    {t("landing.ai.emailChip1")}
                                                </Box>
                                                <Box
                                                    sx={{
                                                        background: "rgba(255,179,0,0.16)",
                                                        color: "#b45309",
                                                        borderRadius: "100px",
                                                        px: 1.4,
                                                        py: 0.5,
                                                        fontFamily: "'Inter', sans-serif",
                                                        fontWeight: 700,
                                                        fontSize: "0.72rem",
                                                    }}
                                                >
                                                    {t("landing.ai.emailChip2")}
                                                </Box>
                                            </Stack>
                                        </Box>
                                    </Box>
                                </Reveal>
                            </Grid>
                        </Grid>
                    </Container>
                </Section>

                {/* ── Customer accountability scoring ── */}
                <Section tone="light">
                    <Container maxWidth="lg">
                        <Reveal>
                            <SectionLabel>
                                {t("landing.scoring.label")}
                            </SectionLabel>
                            <SectionTitle>
                                {t("landing.scoring.title")}
                            </SectionTitle>
                            <SectionSub>
                                {t("landing.scoring.subtitle")}
                            </SectionSub>
                        </Reveal>
                        <Grid container spacing={3}>
                            {SCORING_VISUALS.map((v, i) => (
                                <Grid item xs={12} md={6} key={i}>
                                    <Reveal delay={(i % 2) * 0.1}>
                                        <FeatureCard accent={v.accent}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "flex-start",
                                                    gap: 1.8,
                                                    mb: 2.5,
                                                }}
                                            >
                                                <IconWrap
                                                    accent={v.accent}
                                                    sx={{ mb: 0, flexShrink: 0 }}
                                                >
                                                    {v.icon}
                                                </IconWrap>
                                                <Box>
                                                    <Typography
                                                        sx={{
                                                            fontFamily:
                                                                "'Montserrat', sans-serif",
                                                            fontWeight: 700,
                                                            fontSize: "1.04rem",
                                                            color: colors.primary,
                                                            mb: 0.6,
                                                        }}
                                                    >
                                                        {scoringCards[i]?.title}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontFamily:
                                                                "'Inter', sans-serif",
                                                            fontSize: "0.88rem",
                                                            color: "#5b6472",
                                                            lineHeight: 1.66,
                                                        }}
                                                    >
                                                        {scoringCards[i]?.desc}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* ── Visual mockups per card ── */}
                                            {i === 0 && (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 3,
                                                        background: "#f6f8fc",
                                                        border: "1px solid rgba(4,13,69,0.06)",
                                                        borderRadius: "14px",
                                                        p: 2.4,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            position: "relative",
                                                            width: 96,
                                                            height: 96,
                                                            borderRadius: "50%",
                                                            background:
                                                                "conic-gradient(#1ec195 0% 88%, #e6eaf2 88% 100%)",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 72,
                                                                height: 72,
                                                                borderRadius: "50%",
                                                                background: "#fff",
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                boxShadow:
                                                                    "inset 0 0 0 1px rgba(4,13,69,0.05)",
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontFamily:
                                                                        "'Montserrat', sans-serif",
                                                                    fontWeight: 800,
                                                                    fontSize: "1.5rem",
                                                                    color: "#1ec195",
                                                                    lineHeight: 1,
                                                                }}
                                                            >
                                                                A
                                                            </Typography>
                                                            <Typography
                                                                sx={{
                                                                    fontFamily:
                                                                        "'Inter', sans-serif",
                                                                    fontSize: "0.62rem",
                                                                    fontWeight: 700,
                                                                    color: "#9aa3b2",
                                                                }}
                                                            >
                                                                88{t("landing.scoring.scoreSuffix")}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                fontFamily:
                                                                    "'Inter', sans-serif",
                                                                fontSize: "0.68rem",
                                                                fontWeight: 700,
                                                                textTransform: "uppercase",
                                                                letterSpacing: "0.08em",
                                                                color: "#9aa3b2",
                                                                mb: 1,
                                                            }}
                                                        >
                                                            {t("landing.scoring.grade")}
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                gap: 0.7,
                                                                background:
                                                                    "rgba(30,193,149,0.12)",
                                                                borderRadius: "100px",
                                                                px: 1.4,
                                                                py: 0.5,
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: 7,
                                                                    height: 7,
                                                                    borderRadius: "50%",
                                                                    background: "#1ec195",
                                                                }}
                                                            />
                                                            <Typography
                                                                sx={{
                                                                    fontFamily:
                                                                        "'Inter', sans-serif",
                                                                    fontSize: "0.78rem",
                                                                    fontWeight: 700,
                                                                    color: "#0f9b76",
                                                                }}
                                                            >
                                                                {t("landing.scoring.tierHigh")}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            )}

                                            {i === 1 && (
                                                <Stack spacing={1.3}>
                                                    {RELIABILITY_ROWS.map((r) => (
                                                        <Box
                                                            key={r.name}
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 1.4,
                                                                background: "#f6f8fc",
                                                                border: "1px solid rgba(4,13,69,0.06)",
                                                                borderRadius: "12px",
                                                                p: 1.3,
                                                            }}
                                                        >
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent:
                                                                            "space-between",
                                                                        gap: 1,
                                                                        mb: 0.6,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        noWrap
                                                                        sx={{
                                                                            fontFamily:
                                                                                "'Inter', sans-serif",
                                                                            fontWeight: 600,
                                                                            fontSize: "0.8rem",
                                                                            color: colors.primary,
                                                                        }}
                                                                    >
                                                                        {r.name}
                                                                    </Typography>
                                                                    <Typography
                                                                        sx={{
                                                                            fontFamily:
                                                                                "'Montserrat', sans-serif",
                                                                            fontWeight: 800,
                                                                            fontSize: "0.8rem",
                                                                            color: r.tone,
                                                                            flexShrink: 0,
                                                                        }}
                                                                    >
                                                                        {r.pct}%{" "}
                                                                        <Box
                                                                            component="span"
                                                                            sx={{
                                                                                fontFamily:
                                                                                    "'Inter', sans-serif",
                                                                                fontWeight: 500,
                                                                                fontSize: "0.62rem",
                                                                                color: "#9aa3b2",
                                                                            }}
                                                                        >
                                                                            {t(
                                                                                "landing.scoring.onTime",
                                                                            )}
                                                                        </Box>
                                                                    </Typography>
                                                                </Box>
                                                                <Box
                                                                    sx={{
                                                                        height: 6,
                                                                        borderRadius: "100px",
                                                                        background: "#e6eaf2",
                                                                        overflow: "hidden",
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            height: "100%",
                                                                            width: `${r.pct}%`,
                                                                            borderRadius: "100px",
                                                                            background: r.tone,
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                            <Box
                                                                sx={{
                                                                    background: `${r.tone}1a`,
                                                                    color: r.tone,
                                                                    borderRadius: "100px",
                                                                    px: 1.2,
                                                                    py: 0.4,
                                                                    fontFamily:
                                                                        "'Inter', sans-serif",
                                                                    fontWeight: 700,
                                                                    fontSize: "0.66rem",
                                                                    flexShrink: 0,
                                                                }}
                                                            >
                                                                {tierLabels[r.tier]}
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            )}

                                            {i === 2 && (
                                                <Box
                                                    sx={{
                                                        background: "#f6f8fc",
                                                        border: "1px solid rgba(4,13,69,0.06)",
                                                        borderRadius: "14px",
                                                        p: 2.2,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            justifyContent: "flex-end",
                                                            mb: 1.2,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                gap: 0.6,
                                                                background:
                                                                    "rgba(167,139,250,0.12)",
                                                                borderRadius: "100px",
                                                                px: 1.2,
                                                                py: 0.4,
                                                            }}
                                                        >
                                                            <BoltIcon
                                                                sx={{
                                                                    fontSize: "0.8rem",
                                                                    color: "#a78bfa",
                                                                }}
                                                            />
                                                            <Typography
                                                                sx={{
                                                                    fontFamily:
                                                                        "'Inter', sans-serif",
                                                                    fontWeight: 700,
                                                                    fontSize: "0.64rem",
                                                                    color: "#7c5cff",
                                                                }}
                                                            >
                                                                {t("landing.scoring.flowAuto")}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    {[
                                                        {
                                                            label: t("landing.scoring.flow1"),
                                                            done: true,
                                                        },
                                                        {
                                                            label: t("landing.scoring.flow2"),
                                                            done: true,
                                                        },
                                                        {
                                                            label: t("landing.scoring.flow3"),
                                                            done: false,
                                                        },
                                                    ].map((step, si, arr) => (
                                                        <Box
                                                            key={si}
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 1.4,
                                                                position: "relative",
                                                                pb:
                                                                    si < arr.length - 1
                                                                        ? 1.6
                                                                        : 0,
                                                            }}
                                                        >
                                                            {si < arr.length - 1 && (
                                                                <Box
                                                                    sx={{
                                                                        position: "absolute",
                                                                        left: 13,
                                                                        top: 26,
                                                                        bottom: 4,
                                                                        width: 2,
                                                                        background: step.done
                                                                            ? "#1ec195"
                                                                            : "repeating-linear-gradient(180deg, #c8cfdb 0 4px, transparent 4px 8px)",
                                                                    }}
                                                                />
                                                            )}
                                                            <Box
                                                                sx={{
                                                                    width: 28,
                                                                    height: 28,
                                                                    borderRadius: "50%",
                                                                    flexShrink: 0,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    zIndex: 1,
                                                                    background: step.done
                                                                        ? "#1ec195"
                                                                        : "#fff",
                                                                    border: step.done
                                                                        ? "none"
                                                                        : "2px solid #c8cfdb",
                                                                    color: step.done
                                                                        ? "#fff"
                                                                        : "#9aa3b2",
                                                                }}
                                                            >
                                                                {step.done ? (
                                                                    <CheckIcon
                                                                        sx={{ fontSize: "0.95rem" }}
                                                                    />
                                                                ) : (
                                                                    <Box
                                                                        sx={{
                                                                            width: 8,
                                                                            height: 8,
                                                                            borderRadius: "50%",
                                                                            background: "#c8cfdb",
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                            <Typography
                                                                sx={{
                                                                    fontFamily:
                                                                        "'Inter', sans-serif",
                                                                    fontWeight: step.done
                                                                        ? 600
                                                                        : 500,
                                                                    fontSize: "0.84rem",
                                                                    color: step.done
                                                                        ? colors.primary
                                                                        : "#9aa3b2",
                                                                }}
                                                            >
                                                                {step.label}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}

                                            {i === 3 && (
                                                <Box
                                                    sx={{
                                                        background: "#f6f8fc",
                                                        border: "1px solid rgba(4,13,69,0.06)",
                                                        borderRadius: "14px",
                                                        p: 2.2,
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontFamily: "'Inter', sans-serif",
                                                            fontSize: "0.66rem",
                                                            fontWeight: 700,
                                                            textTransform: "uppercase",
                                                            letterSpacing: "0.08em",
                                                            color: "#9aa3b2",
                                                            mb: 0.5,
                                                        }}
                                                    >
                                                        {t(
                                                            "landing.scoring.metricRecovered",
                                                        )}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontFamily:
                                                                "'Montserrat', sans-serif",
                                                            fontWeight: 800,
                                                            fontSize: "1.7rem",
                                                            color: colors.primary,
                                                            lineHeight: 1,
                                                            mb: 1.6,
                                                        }}
                                                    >
                                                        <StatCounter to={48200} prefix="$" />
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "flex-end",
                                                            gap: 0.8,
                                                            height: 54,
                                                            mb: 1.8,
                                                        }}
                                                    >
                                                        {METRIC_SPARK.map((h, hi) => (
                                                            <Box
                                                                key={hi}
                                                                sx={{
                                                                    flex: 1,
                                                                    height: `${h}%`,
                                                                    borderRadius: "5px 5px 2px 2px",
                                                                    background:
                                                                        hi === METRIC_SPARK.length - 1
                                                                            ? "linear-gradient(180deg, #ffd02e, #ffaa00)"
                                                                            : "rgba(20,58,162,0.18)",
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                    <Grid container spacing={1.2}>
                                                        <Grid item xs={6}>
                                                            <Box
                                                                sx={{
                                                                    background: "#fff",
                                                                    border: "1px solid rgba(4,13,69,0.05)",
                                                                    borderRadius: "10px",
                                                                    p: 1.2,
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily:
                                                                            "'Montserrat', sans-serif",
                                                                        fontWeight: 800,
                                                                        fontSize: "1.1rem",
                                                                        color: "#1ec195",
                                                                        lineHeight: 1,
                                                                    }}
                                                                >
                                                                    82%
                                                                </Typography>
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily:
                                                                            "'Inter', sans-serif",
                                                                        fontSize: "0.64rem",
                                                                        color: "#8a93a3",
                                                                        mt: 0.3,
                                                                    }}
                                                                >
                                                                    {t(
                                                                        "landing.scoring.metricRate",
                                                                    )}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Box
                                                                sx={{
                                                                    background: "#fff",
                                                                    border: "1px solid rgba(4,13,69,0.05)",
                                                                    borderRadius: "10px",
                                                                    p: 1.2,
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily:
                                                                            "'Montserrat', sans-serif",
                                                                        fontWeight: 800,
                                                                        fontSize: "1.1rem",
                                                                        color: "#143AA2",
                                                                        lineHeight: 1,
                                                                    }}
                                                                >
                                                                    11
                                                                </Typography>
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily:
                                                                            "'Inter', sans-serif",
                                                                        fontSize: "0.64rem",
                                                                        color: "#8a93a3",
                                                                        mt: 0.3,
                                                                    }}
                                                                >
                                                                    {t(
                                                                        "landing.scoring.metricDays",
                                                                    )}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            )}
                                        </FeatureCard>
                                    </Reveal>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </Section>

                {/* ── Impact / stats ── */}
                <Section tone="dark">
                    <Orb
                        sx={{
                            top: "-100px",
                            right: "-80px",
                            width: 360,
                            height: 360,
                            background:
                                "radial-gradient(circle, rgba(255,204,0,0.10) 0%, transparent 70%)",
                            animation: `${spinSlow} 40s linear infinite`,
                        }}
                    />
                    <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
                        <Reveal>
                            <SectionLabel ondark={1}>{t("landing.stats.label")}</SectionLabel>
                            <SectionTitle ondark={1}>
                                {t("landing.stats.title")}
                            </SectionTitle>
                            <SectionSub ondark={1}>
                                {t("landing.stats.subtitle")}
                            </SectionSub>
                        </Reveal>

                        <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
                            <Grid item xs={12} md={5}>
                                <Reveal>
                                    <Grid container spacing={2}>
                                        {STAT_VISUALS.map((s, i) => (
                                            <Grid item xs={6} key={i}>
                                                <Box
                                                    sx={{
                                                        background: "rgba(255,255,255,0.04)",
                                                        border: "1px solid rgba(255,255,255,0.08)",
                                                        borderRadius: "14px",
                                                        p: 2.2,
                                                        height: "100%",
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontFamily:
                                                                "'Montserrat', sans-serif",
                                                            fontWeight: 800,
                                                            fontSize: "clamp(1.6rem, 4vw, 2.1rem)",
                                                            color: "#ffcc00",
                                                            lineHeight: 1,
                                                            mb: 1,
                                                        }}
                                                    >
                                                        <StatCounter
                                                            to={s.to}
                                                            prefix={s.prefix}
                                                            suffix={s.suffix}
                                                        />
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: "'Inter', sans-serif",
                                                            fontSize: "0.76rem",
                                                            color: "rgba(255,255,255,0.55)",
                                                            lineHeight: 1.5,
                                                        }}
                                                    >
                                                        {statLabels[i]}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Reveal>
                            </Grid>

                            <Grid item xs={12} md={7}>
                                <Box ref={impactRef}>
                                    <Stack spacing={3.2}>
                                        {(barLabels as string[]).map((label, i) => (
                                            <Box
                                                key={i}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "flex-start",
                                                    gap: 1.4,
                                                    opacity: barsVisible ? 1 : 0,
                                                    transform: barsVisible
                                                        ? "translateY(0)"
                                                        : "translateY(8px)",
                                                    transition: `opacity 0.5s ease ${i * 0.12}s, transform 0.5s ease ${i * 0.12}s`,
                                                }}
                                            >
                                                <CheckCircleIcon
                                                    sx={{
                                                        fontSize: "1.3rem",
                                                        color: "#1ec195",
                                                        flexShrink: 0,
                                                        mt: "1px",
                                                    }}
                                                />
                                                <Typography
                                                    sx={{
                                                        fontFamily: "'Inter', sans-serif",
                                                        fontSize: "0.98rem",
                                                        color: "rgba(255,255,255,0.82)",
                                                        lineHeight: 1.5,
                                                    }}
                                                >
                                                    {label}
                                                </Typography>
                                            </Box>
                                        ))}
                                        <Typography
                                            sx={{
                                                fontFamily: "'Inter', sans-serif",
                                                fontSize: "0.66rem",
                                                color: "rgba(255,255,255,0.28)",
                                                letterSpacing: "0.03em",
                                            }}
                                        >
                                            {t("landing.stats.footnote")}
                                        </Typography>
                                    </Stack>
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>
                </Section>

                {/* ── Control / safety ── */}
                <Section tone="light">
                    <Container maxWidth="md">
                        <Reveal>
                            <Box
                                sx={{
                                    background:
                                        "linear-gradient(135deg, #f6f8fc 0%, #eefcf6 100%)",
                                    border: "1px solid rgba(30,193,149,0.18)",
                                    borderRadius: "22px",
                                    p: { xs: 3.5, md: 5 },
                                    textAlign: "center",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 58,
                                        height: 58,
                                        borderRadius: "16px",
                                        background:
                                            "linear-gradient(135deg, #1ec195, #143AA2)",
                                        color: "#fff",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        mx: "auto",
                                        mb: 2.5,
                                        boxShadow: "0 12px 28px rgba(20,58,162,0.25)",
                                    }}
                                >
                                    <ShieldOutlinedIcon sx={{ fontSize: 28 }} />
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily: "'Montserrat', sans-serif",
                                        fontWeight: 800,
                                        fontSize: "clamp(1.4rem, 3vw, 1.9rem)",
                                        color: colors.primary,
                                        mb: 1.5,
                                    }}
                                >
                                    {t("landing.control.title")}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: "'Inter', sans-serif",
                                        fontSize: "1rem",
                                        color: "#5b6472",
                                        lineHeight: 1.74,
                                        maxWidth: "560px",
                                        mx: "auto",
                                        mb: 3.5,
                                    }}
                                >
                                    {t("landing.control.body")}
                                </Typography>
                                <Grid container spacing={2} sx={{ textAlign: "left" }}>
                                    {controlPoints.map((p) => (
                                        <Grid item xs={12} sm={6} key={p}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1.4,
                                                    background: "#fff",
                                                    border: "1px solid rgba(4,13,69,0.06)",
                                                    borderRadius: "12px",
                                                    p: 1.8,
                                                    height: "100%",
                                                }}
                                            >
                                                <CheckCircleIcon
                                                    sx={{
                                                        fontSize: "1.2rem",
                                                        color: colors.mainSecondary,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Typography
                                                    sx={{
                                                        fontFamily: "'Inter', sans-serif",
                                                        fontSize: "0.9rem",
                                                        color: "#374151",
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {p}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </Reveal>
                    </Container>
                </Section>

                {/* ── Founding-customer onboarding ── */}
                <Section tone="light">
                    <Container maxWidth="md">
                        <Reveal>
                            <SectionLabel>
                                {t("landing.onboarding.label")}
                            </SectionLabel>
                            <SectionTitle>
                                {t("landing.onboarding.title")}
                            </SectionTitle>
                            <SectionSub>
                                {t("landing.onboarding.subtitle")}
                            </SectionSub>
                        </Reveal>
                        <Reveal delay={0.05}>
                            <Box
                                sx={{
                                    background:
                                        "linear-gradient(135deg, #f6f8fc 0%, #eefcf6 100%)",
                                    border: "1px solid rgba(30,193,149,0.18)",
                                    borderRadius: "22px",
                                    p: { xs: 3, md: 4.5 },
                                }}
                            >
                                <Grid container spacing={2}>
                                    {onboardingItems.map((item) => (
                                        <Grid item xs={12} sm={6} key={item}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "flex-start",
                                                    gap: 1.4,
                                                }}
                                            >
                                                <CheckCircleIcon
                                                    sx={{
                                                        fontSize: "1.3rem",
                                                        color: colors.mainSecondary,
                                                        flexShrink: 0,
                                                        mt: "1px",
                                                    }}
                                                />
                                                <Typography
                                                    sx={{
                                                        fontFamily:
                                                            "'Inter', sans-serif",
                                                        fontSize: "0.96rem",
                                                        color: "#374151",
                                                        fontWeight: 500,
                                                        lineHeight: 1.6,
                                                    }}
                                                >
                                                    {item}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                                <Typography
                                    sx={{
                                        mt: 3.5,
                                        pt: 3,
                                        borderTop: "1px solid rgba(4,13,69,0.08)",
                                        fontFamily: "'Inter', sans-serif",
                                        fontSize: "0.96rem",
                                        color: "#5b6472",
                                        lineHeight: 1.72,
                                        fontStyle: "italic",
                                    }}
                                >
                                    {t("landing.onboarding.note")}
                                </Typography>
                            </Box>
                        </Reveal>
                    </Container>
                </Section>

                {/* ── FAQ ── */}
                <Section id="faq" tone="tint">
                    <Container maxWidth="md">
                        <Reveal>
                            <SectionLabel>{t("landing.faq.label")}</SectionLabel>
                            <SectionTitle>{t("landing.faq.title")}</SectionTitle>
                            <SectionSub>{t("landing.faq.subtitle")}</SectionSub>
                        </Reveal>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.3,
                            }}
                        >
                            {faqItems.map((item, i) => (
                                <Reveal delay={(i % 4) * 0.05} key={item.q}>
                                    <Box
                                        component="details"
                                        sx={{
                                            background: "#fff",
                                            border: "1px solid rgba(4,13,69,0.08)",
                                            borderRadius: "14px",
                                            px: { xs: 2.2, md: 2.8 },
                                            boxShadow:
                                                "0 1px 2px rgba(4,13,69,0.04)",
                                            "&[open] .faqToggle": {
                                                transform: "rotate(45deg)",
                                            },
                                        }}
                                    >
                                        <Box
                                            component="summary"
                                            sx={{
                                                listStyle: "none",
                                                cursor: "pointer",
                                                py: 2,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: 2,
                                                fontFamily:
                                                    "'Montserrat', sans-serif",
                                                fontWeight: 700,
                                                fontSize: "1rem",
                                                color: colors.primary,
                                                "&::-webkit-details-marker": {
                                                    display: "none",
                                                },
                                            }}
                                        >
                                            {item.q}
                                            <Box
                                                className="faqToggle"
                                                sx={{
                                                    flexShrink: 0,
                                                    fontSize: "1.5rem",
                                                    lineHeight: 1,
                                                    color: colors.mainSecondary,
                                                    transition:
                                                        "transform 0.2s ease",
                                                }}
                                            >
                                                +
                                            </Box>
                                        </Box>
                                        <Typography
                                            sx={{
                                                pb: 2.2,
                                                fontFamily: "'Inter', sans-serif",
                                                fontSize: "0.94rem",
                                                color: "#5b6472",
                                                lineHeight: 1.72,
                                            }}
                                        >
                                            {item.a}
                                        </Typography>
                                    </Box>
                                </Reveal>
                            ))}
                        </Box>
                        <Reveal delay={0.1}>
                            <Box sx={{ textAlign: "center", mt: 4 }}>
                                <Button
                                    onClick={() => navigate("/business/pricing")}
                                    endIcon={<ArrowForwardIcon />}
                                    sx={{
                                        fontFamily: "'Inter', sans-serif",
                                        fontWeight: 700,
                                        fontSize: "0.94rem",
                                        textTransform: "none",
                                        color: colors.blueRibbon,
                                        background: "#fff",
                                        border: "1px solid rgba(20,58,162,0.25)",
                                        px: 2.8,
                                        py: 1.1,
                                        borderRadius: "999px",
                                        "&:hover": {
                                            background: "rgba(20,58,162,0.06)",
                                        },
                                    }}
                                >
                                    {t("landing.faq.ctaText")}
                                </Button>
                            </Box>
                        </Reveal>
                    </Container>
                </Section>

                {/* ── Final CTA ── */}
                <Box
                    id="pricing"
                    sx={{
                        position: "relative",
                        overflow: "hidden",
                        background:
                            "linear-gradient(125deg, #070e29 0%, #0d1636 50%, #16245c 100%)",
                        backgroundSize: "200% 200%",
                        animation: `${gradientPan} 18s ease infinite`,
                        py: { xs: 8, md: 12 },
                        px: 2.5,
                        textAlign: "center",
                    }}
                >
                    <Orb
                        sx={{
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%,-50%)",
                            width: 520,
                            height: 320,
                            background:
                                "radial-gradient(circle, rgba(255,176,0,0.16) 0%, transparent 70%)",
                        }}
                    />
                    <Container
                        maxWidth="sm"
                        sx={{ position: "relative", zIndex: 1 }}
                    >
                        <Reveal>
                            <Badge>
                                <AutoAwesomeIcon sx={{ fontSize: "0.9rem" }} />
                                {t("landing.finalCta.badge")}
                            </Badge>
                            <Typography
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontSize: "clamp(1.8rem, 4.5vw, 2.8rem)",
                                    fontWeight: 800,
                                    color: "#fff",
                                    lineHeight: 1.14,
                                    letterSpacing: "-0.02em",
                                    mt: 2.5,
                                    mb: 2,
                                }}
                            >
                                {t("landing.finalCta.titleLead")}{" "}
                                <GoldSpan>{t("landing.finalCta.titleAccent")}</GoldSpan>
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: "'Inter', sans-serif",
                                    fontSize: "1.04rem",
                                    color: "rgba(255,255,255,0.66)",
                                    lineHeight: 1.74,
                                    mb: 4,
                                }}
                            >
                                {t("landing.finalCta.body")}
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 1.2,
                                    mb: 3.5,
                                }}
                            >
                                <GoldButton
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={() => navigate("/collect/sign-up")}
                                    sx={{ whiteSpace: "nowrap" }}
                                >
                                    {t("landing.finalCta.ctaPrimary")}
                                </GoldButton>
                                <Typography
                                    sx={{
                                        fontFamily: "'Inter', sans-serif",
                                        fontSize: "0.86rem",
                                        color: "rgba(255,255,255,0.72)",
                                    }}
                                >
                                    {t("landing.hero.trialNote")}
                                </Typography>
                            </Box>
                            {renderDemoForm(true)}
                            <Typography
                                sx={{
                                    fontFamily: "'Inter', sans-serif",
                                    fontSize: "0.78rem",
                                    color: "rgba(255,255,255,0.4)",
                                    mt: 3,
                                }}
                            >
                                {t("landing.finalCta.footnote")}
                            </Typography>
                        </Reveal>
                    </Container>
                </Box>

                {/* ── Integrations ── */}
                <Section id="integrations" tone="tint">
                    <Container maxWidth="lg">
                        <Reveal>
                            <SectionLabel>
                                {t("landing.integrations.label")}
                            </SectionLabel>
                            <SectionTitle>
                                {t("landing.integrations.title")}
                            </SectionTitle>
                            <SectionSub>
                                {t("landing.integrations.subtitle")}
                            </SectionSub>
                        </Reveal>

                        {/* Live integration — QuickBooks Online */}
                        <Reveal delay={0.05}>
                            <Box
                                sx={{
                                    maxWidth: 720,
                                    mx: "auto",
                                    mb: { xs: 4, md: 6 },
                                    p: { xs: 3, md: 4 },
                                    borderRadius: "22px",
                                    background: "#ffffff",
                                    border: `1.5px solid ${colors.mainSecondary}`,
                                    boxShadow:
                                        "0 24px 50px -28px rgba(30,193,149,0.45)",
                                    textAlign: "center",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.9,
                                        px: 1.6,
                                        py: 0.6,
                                        mb: 2,
                                        borderRadius: "999px",
                                        background: "rgba(30,193,149,0.12)",
                                        border: "1px solid rgba(30,193,149,0.35)",
                                        fontFamily: "'Inter', sans-serif",
                                        fontSize: "0.74rem",
                                        fontWeight: 700,
                                        letterSpacing: "0.04em",
                                        textTransform: "uppercase",
                                        color: "#0b7a5e",
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            width: 9,
                                            height: 9,
                                            borderRadius: "50%",
                                            background: colors.mainSecondary,
                                            boxShadow: `0 0 0 0 ${colors.mainSecondary}`,
                                            animation: `${pulseRing} 2s infinite`,
                                        }}
                                    />
                                    {t("landing.integrations.liveLabel")}
                                </Box>
                                <Box
                                    sx={{
                                        width: 72,
                                        height: 72,
                                        mx: "auto",
                                        mb: 2,
                                        borderRadius: "18px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "#f6f8fc",
                                        border: "1px solid rgba(4,13,69,0.06)",
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src="https://logo.clearbit.com/quickbooks.intuit.com"
                                        alt="QuickBooks Online logo"
                                        loading="lazy"
                                        onError={(e) => {
                                            const img =
                                                e.currentTarget as HTMLImageElement;
                                            img.onerror = null;
                                            img.src =
                                                "https://www.google.com/s2/favicons?domain=quickbooks.intuit.com&sz=64";
                                        }}
                                        sx={{
                                            width: 46,
                                            height: 46,
                                            objectFit: "contain",
                                        }}
                                    />
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily: "'Montserrat', sans-serif",
                                        fontSize: "clamp(1.4rem, 3vw, 1.9rem)",
                                        fontWeight: 800,
                                        color: colors.primary,
                                        mb: 1.2,
                                    }}
                                >
                                    {t("landing.integrations.live.name")}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: "'Inter', sans-serif",
                                        fontSize: "1rem",
                                        color: "#5b6472",
                                        lineHeight: 1.7,
                                        maxWidth: 540,
                                        mx: "auto",
                                    }}
                                >
                                    {t("landing.integrations.live.desc")}
                                </Typography>
                            </Box>
                        </Reveal>

                        {/* Integration waitlist */}
                        <Reveal delay={0.1}>
                            <Box
                                sx={{
                                    mt: { xs: 4, md: 5 },
                                    maxWidth: 720,
                                    mx: "auto",
                                    textAlign: "center",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 1.6,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: "'Inter', sans-serif",
                                        fontSize: "0.98rem",
                                        color: "#5b6472",
                                        lineHeight: 1.7,
                                    }}
                                >
                                    {t("landing.integrations.waitlistLine")}
                                </Typography>
                                <Button
                                    onClick={() =>
                                        document
                                            .getElementById("pricing")
                                            ?.scrollIntoView({
                                                behavior: "smooth",
                                                block: "start",
                                            })
                                    }
                                    endIcon={<ArrowForwardIcon />}
                                    sx={{
                                        fontFamily: "'Inter', sans-serif",
                                        fontWeight: 700,
                                        fontSize: "0.92rem",
                                        textTransform: "none",
                                        color: colors.blueRibbon,
                                        background: "#fff",
                                        border: "1px solid rgba(20,58,162,0.25)",
                                        px: 2.6,
                                        py: 1,
                                        borderRadius: "999px",
                                        "&:hover": {
                                            background: "rgba(20,58,162,0.06)",
                                        },
                                    }}
                                >
                                    {t("landing.integrations.waitlistCta")}
                                </Button>
                            </Box>
                        </Reveal>

                        {/* CSV import */}
                        <Reveal delay={0.15}>
                            <Box
                                sx={{
                                    mt: { xs: 4, md: 6 },
                                    maxWidth: 720,
                                    mx: "auto",
                                    p: { xs: 2.5, md: 3 },
                                    borderRadius: "18px",
                                    background: "rgba(20,58,162,0.05)",
                                    border: "1px dashed rgba(20,58,162,0.3)",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    flexWrap: "wrap",
                                    justifyContent: "center",
                                    textAlign: { xs: "center", sm: "left" },
                                }}
                            >
                                <Box
                                    sx={{
                                        flexShrink: 0,
                                        width: 48,
                                        height: 48,
                                        borderRadius: "12px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "rgba(20,58,162,0.1)",
                                        color: colors.blueRibbon,
                                    }}
                                >
                                    <UploadFileOutlinedIcon />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 220 }}>
                                    <Typography
                                        sx={{
                                            fontFamily:
                                                "'Montserrat', sans-serif",
                                            fontSize: "1.05rem",
                                            fontWeight: 700,
                                            color: colors.primary,
                                            mb: 0.4,
                                        }}
                                    >
                                        {t("landing.integrations.csvTitle")}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Inter', sans-serif",
                                            fontSize: "0.94rem",
                                            color: "#5b6472",
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        {t("landing.integrations.csvDesc")}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.7,
                                        px: 1.6,
                                        py: 0.7,
                                        borderRadius: "999px",
                                        background: "#ffffff",
                                        border: "1px solid rgba(20,58,162,0.25)",
                                        fontFamily: "'Inter', sans-serif",
                                        fontSize: "0.86rem",
                                        fontWeight: 700,
                                        color: colors.blueRibbon,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    <LinkOutlinedIcon
                                        sx={{ fontSize: "1.05rem" }}
                                    />
                                    {t("landing.integrations.csvLabel")}
                                </Box>
                            </Box>
                        </Reveal>
                    </Container>
                </Section>

                <Footer variant="recover" />
            </PageWrapper>
        </>
    );
};

export default BusinessLandingPage;
